import React, { useMemo } from 'react';

/**
 * Modal podgladu importu CSV + XML cennika.
 * CSV = ceny zakupu netto + nazwy
 * XML = ceny detaliczne brutto + stawki VAT
 * Laczenie po EAN.
 */
const CsvXmlPricelistModal = ({
  preview, options, setOptions, search, setSearch, filter, setFilter,
  isSaving, onSave, onClose
}) => {
  const items = preview?.preview || [];
  const stats = preview?.stats || {};

  const filtered = useMemo(() => {
    if (!preview) return [];
    let list = items;
    if (filter === 'matched') list = list.filter(p => !p.is_new);
    else if (filter === 'new') list = list.filter(p => p.is_new);
    else if (filter === 'changed') list = list.filter(p => p.changes?.length > 0);
    else if (filter === 'purchase') list = list.filter(p => p.changes?.includes('cena_zakupu'));
    else if (filter === 'retail') list = list.filter(p => p.changes?.includes('cena_detaliczna'));
    else if (filter === 'vat') list = list.filter(p => p.changes?.includes('stawka_vat'));
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(p =>
        (p.ean || '').toLowerCase().includes(s) ||
        (p.nazwa_csv || '').toLowerCase().includes(s) ||
        (p.nazwa_db || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [preview, items, filter, search]);

  const changedCount = items.filter(p => p.changes?.length > 0).length;

  if (!preview) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px',
        width: '95vw', maxWidth: '1200px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: 'var(--bg)'
        }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '16px' }}>
              Import CSV + XML cennika
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              CSV: {preview.csv_count || 0} produktow | XML: {preview.xml_count || 0} produktow
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
            color: 'var(--text-muted)', padding: '4px 8px'
          }}>x</button>
        </div>

        {/* Stats */}
        <div style={{
          padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)',
          display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center'
        }}>
          <StatBadge label="Dopasowane" value={stats.matched || 0} color="var(--success)" />
          <StatBadge label="Nowe" value={stats.new_products || 0} color="var(--info)" />
          <StatBadge label="Ze zmianami" value={changedCount} color="var(--warning)" />
          <StatBadge label="Pominiete" value={stats.skipped || 0} color="var(--text-muted)" />

          <div style={{ flex: 1 }} />

          {/* Search */}
          <input
            type="text" placeholder="Szukaj EAN / nazwa..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              padding: '6px 10px', fontSize: '12px', borderRadius: '6px',
              border: '1px solid var(--border)', width: '200px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)'
            }}
          />
        </div>

        {/* Filters + Options */}
        <div style={{
          padding: '0.5rem 1.25rem', borderBottom: '1px solid var(--border)',
          display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center'
        }}>
          {/* Filter tabs */}
          {[
            ['all', 'Wszystkie'],
            ['matched', 'Dopasowane'],
            ['new', 'Nowe'],
            ['changed', 'Ze zmianami'],
            ['purchase', 'Cena zakupu'],
            ['retail', 'Cena detal.'],
            ['vat', 'VAT'],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} style={{
              padding: '4px 10px', fontSize: '11px',
              border: filter === key ? '1px solid var(--primary)' : '1px solid var(--border)',
              backgroundColor: filter === key ? 'var(--primary)' : 'var(--card-bg)',
              color: filter === key ? '#fff' : 'var(--text-secondary)',
              cursor: 'pointer', fontWeight: filter === key ? '600' : '400'
            }}>{label}</button>
          ))}

          <div style={{ flex: 1 }} />

          {/* Options */}
          <CheckOption label="Cena zakupu" checked={options.update_purchase_price}
            onChange={v => setOptions(o => ({ ...o, update_purchase_price: v }))} />
          <CheckOption label="Cena detal." checked={options.update_retail_price}
            onChange={v => setOptions(o => ({ ...o, update_retail_price: v }))} />
          <CheckOption label="VAT" checked={options.update_vat}
            onChange={v => setOptions(o => ({ ...o, update_vat: v }))} />
          <CheckOption label="Twórz nowe" checked={options.create_new}
            onChange={v => setOptions(o => ({ ...o, create_new: v }))} />
          <CheckOption label="Nazwy" checked={options.update_names}
            onChange={v => setOptions(o => ({ ...o, update_names: v }))} />
        </div>

        {/* Table */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1 }}>
                <Th>Status</Th>
                <Th>EAN</Th>
                <Th>Nazwa (CSV)</Th>
                <Th>Nazwa (baza)</Th>
                <Th align="right">Zakup netto</Th>
                <Th align="right">Zakup stary</Th>
                <Th align="right">Detal. brutto</Th>
                <Th align="right">Detal. stary</Th>
                <Th align="center">VAT</Th>
                <Th>Zrodlo</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((p, i) => {
                const hasChanges = p.changes?.length > 0;
                return (
                  <tr key={i} style={{
                    borderBottom: '1px solid var(--border)',
                    backgroundColor: p.is_new ? 'rgba(23,162,184,0.05)' :
                      hasChanges ? 'rgba(255,193,7,0.05)' : 'transparent'
                  }}>
                    <Td>
                      {p.is_new ? (
                        <span style={{ color: 'var(--info)', fontWeight: '600' }}>NOWY</span>
                      ) : hasChanges ? (
                        <span style={{ color: 'var(--warning)', fontWeight: '600' }}>ZMIANA</span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>OK</span>
                      )}
                    </Td>
                    <Td><code style={{ fontSize: '10px' }}>{p.ean}</code></Td>
                    <Td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.nazwa_csv || '-'}
                    </Td>
                    <Td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.nazwa_db || '-'}
                    </Td>
                    <PriceTd value={p.cena_zakupu_netto_nowa} changed={p.changes?.includes('cena_zakupu')} />
                    <PriceTd value={p.cena_zakupu_netto_stara} muted />
                    <PriceTd value={p.cena_detaliczna_brutto_nowa} changed={p.changes?.includes('cena_detaliczna')} />
                    <PriceTd value={p.cena_detaliczna_brutto_stara} muted />
                    <Td align="center" style={{
                      color: p.changes?.includes('stawka_vat') ? 'var(--danger)' : 'inherit',
                      fontWeight: p.changes?.includes('stawka_vat') ? '700' : '400'
                    }}>
                      {p.stawka_vat_nowa}%
                    </Td>
                    <Td>
                      {p.w_csv && <span title="CSV" style={{ fontSize: '10px', marginRight: '2px' }}>CSV</span>}
                      {p.w_xml && <span title="XML" style={{ fontSize: '10px' }}>XML</span>}
                    </Td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Brak produktów pasujących do filtra
                </td></tr>
              )}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <div style={{ padding: '0.5rem', textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)' }}>
              Wyswietlono 500 z {filtered.length} produktów
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1.25rem', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: 'var(--bg)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {filtered.length} produktów w tabeli | {changedCount} ze zmianami
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onClose} style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)', cursor: 'pointer',
              backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)'
            }}>Anuluj</button>
            <button onClick={onSave} disabled={isSaving} style={{
              padding: '6px 12px', fontSize: '12px', fontWeight: '600',
              border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer',
              backgroundColor: 'var(--success)', color: '#fff',
              opacity: isSaving ? 0.6 : 1
            }}>
              {isSaving ? 'Zapisywanie...' : `Zastosuj zmiany (${changedCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper components
const StatBadge = ({ label, value, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.25rem 0.5rem', borderRadius: '4px',
    backgroundColor: `${color}15`, border: `1px solid ${color}30`
  }}>
    <span style={{ fontWeight: '700', fontSize: '14px', color }}>{value}</span>
    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{label}</span>
  </div>
);

const CheckOption = ({ label, checked, onChange }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: '0.25rem',
    fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer'
  }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      style={{ width: '13px', height: '13px', cursor: 'pointer' }} />
    {label}
  </label>
);

const Th = ({ children, align = 'left' }) => (
  <th style={{
    padding: '0.5rem 0.4rem', textAlign: align, fontWeight: '600',
    borderBottom: '2px solid var(--border)', fontSize: '10px',
    color: 'var(--text-muted)', textTransform: 'uppercase'
  }}>{children}</th>
);

const Td = ({ children, align = 'left', style = {} }) => (
  <td style={{ padding: '0.35rem 0.4rem', textAlign: align, ...style }}>{children}</td>
);

const PriceTd = ({ value, changed, muted }) => {
  const v = parseFloat(value) || 0;
  return (
    <td style={{
      padding: '0.35rem 0.4rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px',
      color: changed ? 'var(--warning)' : muted ? 'var(--text-muted)' : 'inherit',
      fontWeight: changed ? '700' : '400'
    }}>
      {v > 0 ? v.toFixed(2) : '-'}
    </td>
  );
};

export default CsvXmlPricelistModal;
